import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { Radio, Phone, Gift, Globe, Play, ArrowRight, Headphones, Heart, Zap } from "lucide-react";
import { MARKETING_CONFIG } from "@/config/marketing";
import radioHero from "@/assets/radio-hero.jpg";
import { useTranslation } from "react-i18next";
import { useCountryRates } from "@/hooks/useCountryRates";
import { getFlagSvgUrl } from "@/lib/country-flags";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";

const FEATURED_COUNTRIES = [
  { flag: "🇸🇸", nameKey: "South Sudan", dialCode: "211" },
  { flag: "🇪🇹", nameKey: "Ethiopia", dialCode: "251" },
  { flag: "🇰🇪", nameKey: "Kenya", dialCode: "254" },
  { flag: "🇳🇬", nameKey: "Nigeria", dialCode: "234" },
  { flag: "🇺🇬", nameKey: "Uganda", dialCode: "256" },
  { flag: "🇸🇴", nameKey: "Somalia", dialCode: "252" },
];

export default function ConnectRadioLanding() {
  const { t } = useTranslation("common");
  const { getRateForCountry, formatRate } = useCountryRates();

  const STEPS = [
    { icon: Play, title: t("tapListen"), description: t("tapListenDesc") },
    { icon: Phone, title: t("callHomeStep"), description: t("callHomeStepDesc") },
    { icon: Gift, title: t("getFreeStep"), description: t("getFreeStepDesc") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={PAGE_SEO.radio.title}
        description={PAGE_SEO.radio.description}
        keywords={PAGE_SEO.radio.keywords}
        canonicalUrl={getCanonicalUrl('/connect-portal/free-radio')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Free Radio', url: 'https://elefa.tel/connect-portal/free-radio' },
        ])}
      />
      {/* Nav */}
      <nav className="container py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo variant="header" size="sm" asLink={false} withIcon fullName />
          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-medium">
            Connect
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/connect-portal/auth">
            <Button variant="ghost" size="sm">{t("signIn")}</Button>
          </Link>
          <Link to="/connect-portal/auth">
            <Button size="sm" className="gap-1.5">
              {t("getFreeCreditBtn")} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={radioHero} alt="Radio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        </div>

        <div className="container relative py-20 md:py-32">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 bg-primary/15 text-primary px-4 py-2 rounded-full mb-6">
              <Radio className="h-4 w-4" />
              <span className="text-sm font-semibold">{t("freeNoAccountNeeded")}</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight mb-4">
              {t("theSoundsOfHome")}
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-md">
              {t("radioFreeDescription")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/connect-portal/radio">
                <Button size="lg" className="gap-2 text-base px-8 w-full sm:w-auto">
                  <Headphones className="h-5 w-5" />
                  {t("listenNowFree")}
                </Button>
              </Link>
              <Link to="/connect-portal/auth">
                <Button size="lg" variant="outline" className="gap-2 text-base px-8 w-full sm:w-auto">
                  <Gift className="h-5 w-5" />
                  {t("signUpGetFreeCredit")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Country Flags Strip */}
      <section className="border-y bg-muted/30">
        <div className="container py-8">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {FEATURED_COUNTRIES.map((c) => {
              const rate = getRateForCountry(c.dialCode);
              const rateDisplay = rate ? formatRate(rate.ratePerMinuteCents) : "";
              return (
                <div key={c.nameKey} className="flex flex-col items-center gap-1">
                  <img src={getFlagSvgUrl(c.flag)} alt="" className="w-10 h-7 object-cover rounded-sm" />
                  <span className="text-xs font-medium text-foreground">{c.nameKey}</span>
                  {rateDisplay && (
                    <span className="text-xs text-primary font-semibold">{t("fromPerMin", { rate: rateDisplay })}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container py-20 md:py-28">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t("radioFirstCallsSecond")}
          </h2>
          <p className="text-muted-foreground text-lg max-w-lg mx-auto">
            {t("radioFirstDescription")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {STEPS.map((step, i) => (
            <div key={i} className="text-center group">
              <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <step.icon className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
              <p className="text-muted-foreground text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Emotional CTA Block */}
      <section className="bg-foreground text-background">
        <div className="container py-20 md:py-28 text-center">
          <Heart className="h-10 w-10 mx-auto mb-6 text-primary" />
          <h2 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
            {t("tenThousandKm")}<br />
            {t("oneTapClose")}
          </h2>
          <p className="text-background/70 text-lg max-w-lg mx-auto mb-8">
            {t("listenToRadio")}
          </p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2 text-base px-10">
              <Phone className="h-5 w-5" />
              {t("startCallingGetFree")}
            </Button>
          </Link>
        </div>
      </section>

      {/* Why elefa Connect */}
      <section className="container py-20 md:py-28">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t("whyDiasporaChoose")}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {[
            { icon: Radio, title: t("freeRadio"), desc: t("freeRadioDesc") },
            { icon: Zap, title: t("hdCalls"), desc: t("hdCallsDescWorldwide", "Clear voice to every country, no app needed on the other side.") },
            { icon: Globe, title: t("noExpiry"), desc: t("noExpiryDesc") },
            { icon: Gift, title: t("freeCreditTitle"), desc: t("freeCreditDesc") },
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-2xl border bg-card hover:shadow-md transition-shadow text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <item.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary/5 border-t">
        <div className="container py-16 md:py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            {t("readyToHearSounds")}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            {t("freeRadioUnbeatableCalls")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/connect-portal/radio">
              <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                <Headphones className="h-5 w-5" />
                {t("listenToFreeRadio")}
              </Button>
            </Link>
            <Link to="/connect-portal/auth">
              <Button size="lg" className="gap-2 w-full sm:w-auto">
                <ArrowRight className="h-5 w-5" />
                {t("signUpGetFreeCreditBtn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <ConnectFooter />
    </div>
  );
}
